import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { lazy, Suspense, useEffect, useState } from 'react';
import { syncAuthTokenFromCookie } from './lib/authToken';
import { CartProvider } from './context/CartContext';
import { removeBootLoader, waitForPageReady } from './lib/pageReady';
import Loader from './components/Loader';
import SmoothScroll from './components/SmoothScroll';
import './App.css';

const HomePage = lazy(() => import('./pages/HomePage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const LegalPage = lazy(() => import('./pages/LegalPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));

function AuthBootstrap({ children }) {
  useEffect(() => {
    syncAuthTokenFromCookie();
  }, []);

  return children;
}

const LIGHT_ROUTES = new Set([
  '/log-in',
  '/login',
  '/registration',
  '/register',
  '/terms-and-conditions',
  '/privacy-policy',
  '/return-and-refund-policy',
  '/shipping-policy',
]);

/**
 * Shows the branded loader on refresh / navigation until the route
 * has mounted and visible images have finished rendering.
 */
function PageReadyGate({ children }) {
  const location = useLocation();
  const isLightRoute = LIGHT_ROUTES.has(location.pathname);
  const [showLoader, setShowLoader] = useState(!isLightRoute);

  useEffect(() => {
    let cancelled = false;

    if (isLightRoute) {
      setShowLoader(false);
      removeBootLoader();
      return undefined;
    }

    setShowLoader(true);

    (async () => {
      try {
        await waitForPageReady({
          minMs: 450,
          maxMs: 12000,
        });
      } catch {
        /* ignore — still hide loader */
      }

      if (!cancelled) {
        setShowLoader(false);
        removeBootLoader();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [location.pathname, location.search, isLightRoute]);

  return (
    <Suspense
      fallback={
        isLightRoute ? null : <Loader size={200} />
      }
    >
      {showLoader ? <Loader size={200} /> : null}
      {children}
    </Suspense>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthBootstrap>
        <CartProvider>
          <PageReadyGate>
            <SmoothScroll>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/log-in" element={<LoginPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/registration" element={<RegisterPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/terms-and-conditions" element={<LegalPage policyKey="terms" />} />
                <Route path="/privacy-policy" element={<LegalPage policyKey="privacy" />} />
                <Route path="/return-and-refund-policy" element={<LegalPage policyKey="refund" />} />
                <Route path="/shipping-policy" element={<LegalPage policyKey="shipping" />} />
              </Routes>
            </SmoothScroll>
          </PageReadyGate>
        </CartProvider>
      </AuthBootstrap>
    </BrowserRouter>
  );
}

export default App;
